/**
 * The colour for an icon drawn ON a leave type's colour: navy on a pale colour, white
 * otherwise (Rob's 4A + 9A, 29/10/2026). A customer can pick any colour in Settings →
 * Leave types; Jury service's pale pink `#fecaca` made the white icon all but vanish.
 *
 * "Pale" = relative luminance (WCAG) above 0.6 — white on it is under 1.6:1. The usual
 * leave colours (amber, red, blue, teal, purple, grey) stay white, as they always were.
 * Anything that isn't a #rgb / #rrggbb colour stays white (the old behaviour).
 * Checked by `node src/lib/leaveColour.checks.mjs` (from `frontend/`).
 */
export function isPaleColour(colour) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(colour || '').trim());
  if (!m) return false;
  const hex = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const lin = (i) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
  return luminance > 0.6;
}

/** Tailwind text class for an icon on `colour`. */
export function iconOn(colour) {
  return isPaleColour(colour) ? 'text-hive-navy' : 'text-white';
}

/**
 * Tailwind text class for a DATE NUMBER on `colour` — shown in place of the icon while the
 * pointer is on a marked day (Rob, 03/11/2026). Words need more contrast than an icon
 * (WCAG 4.5:1), so this picks whichever of navy and white is CLEARER on the colour, rather than
 * the icon's "pale or not" line: on Annual leave's amber `#F59E0B` white is 2.1:1, navy 8.0:1.
 * Anything that isn't a #rgb / #rrggbb colour gets white (as `iconOn`).
 */
export function numberOn(colour) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(colour || '').trim());
  if (!m) return 'text-white';
  const hex = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const lum = (h) => {
    const lin = (i) => {
      const v = parseInt(h.slice(i, i + 2), 16) / 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
  };
  const L = lum(hex);
  const navy = lum('131F3C');
  const onWhite = 1.05 / (L + 0.05);
  const onNavy = (L + 0.05) / (navy + 0.05);
  return onNavy >= onWhite ? 'text-hive-navy' : 'text-white';
}
